<template>
  <div class="user-profile">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">个人中心</h2>
        <p class="page-subtitle">管理您的个人信息和账号设置</p>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="profile-content">
      <el-row :gutter="20">
        <!-- 左侧：头像和基本信息卡片 -->
        <el-col :xs="24" :sm="24" :md="8" :lg="6">
          <el-card shadow="hover" class="avatar-card">
            <div class="avatar-section">
              <el-avatar :size="120" :src="profileForm.avatar" class="user-avatar">
                <el-icon :size="60"><User /></el-icon>
              </el-avatar>
              
              <el-upload
                action="#"
                :auto-upload="false"
                :show-file-list="false"
                :on-change="handleAvatarChange"
                accept="image/*"
                class="avatar-uploader"
              >
                <el-button type="primary" size="small" class="upload-btn">
                  <el-icon><Upload /></el-icon>
                  更换头像
                </el-button>
              </el-upload>
              
              <div class="avatar-tips">
                <el-text size="small" type="info">
                  建议尺寸：200x200像素<br/>
                  支持格式：JPG、PNG、GIF<br/>
                  文件大小：不超过2MB
                </el-text>
              </div>
            </div>
            
            <el-divider />
            
            <div class="user-stats">
              <div class="stat-item">
                <div class="stat-label">注册时间</div>
                <div class="stat-value">{{ userInfo.createdAt || '2024-01-01' }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-label">最后登录</div>
                <div class="stat-value">{{ userInfo.lastLoginAt || '刚刚' }}</div>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- 右侧：详细信息表单 -->
        <el-col :xs="24" :sm="24" :md="16" :lg="18">
          <el-card shadow="hover" class="info-card">
            <template #header>
              <div class="card-header">
                <h3>基本信息</h3>
                <el-text type="info">编辑您的个人资料</el-text>
              </div>
            </template>

            <el-form
              ref="profileFormRef"
              :model="profileForm"
              :rules="profileRules"
              label-width="100px"
              :disabled="formLoading"
            >
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="用户名" prop="username">
                    <el-input
                      v-model="profileForm.username"
                      placeholder="请输入用户名"
                      :prefix-icon="User"
                      clearable
                    />
                  </el-form-item>
                </el-col>

                <el-col :span="12">
                  <el-form-item label="真实姓名" prop="realName">
                    <el-input
                      v-model="profileForm.realName"
                      placeholder="请输入真实姓名"
                      :prefix-icon="User"
                      clearable
                    />
                  </el-form-item>
                </el-col>
              </el-row>

              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="邮箱地址" prop="email">
                    <el-input
                      v-model="profileForm.email"
                      placeholder="请输入邮箱地址"
                      :prefix-icon="Message"
                      clearable
                    />
                  </el-form-item>
                </el-col>

                <el-col :span="12">
                  <el-form-item label="联系电话" prop="phone">
                    <el-input
                      v-model="profileForm.phone"
                      placeholder="请输入联系电话"
                      :prefix-icon="Phone"
                      clearable
                      maxlength="11"
                    />
                  </el-form-item>
                </el-col>
              </el-row>

              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="性别" prop="gender">
                    <el-radio-group v-model="profileForm.gender">
                      <el-radio label="male">男</el-radio>
                      <el-radio label="female">女</el-radio>
                      <el-radio label="other">保密</el-radio>
                    </el-radio-group>
                  </el-form-item>
                </el-col>

                <el-col :span="12">
                  <el-form-item label="角色" prop="role">
                    <el-tag type="success" size="large">
                      {{ roleLabel }}
                    </el-tag>
                  </el-form-item>
                </el-col>
              </el-row>

              <el-form-item label="个人简介" prop="bio">
                <el-input
                  v-model="profileForm.bio"
                  type="textarea"
                  :rows="4"
                  placeholder="介绍一下自己吧..."
                  maxlength="200"
                  show-word-limit
                />
              </el-form-item>

              <el-divider />

              <el-form-item>
                <el-button
                  type="primary"
                  @click="handleSubmit"
                  :loading="formLoading"
                  :icon="Check"
                >
                  保存修改
                </el-button>
                <el-button @click="handleReset" :icon="RefreshLeft">
                  重置
                </el-button>
                <el-button @click="handleCancel" :icon="Close">
                  取消
                </el-button>
              </el-form-item>
            </el-form>
          </el-card>

          <!-- 密码修改 -->
          <el-card shadow="hover" class="password-card" style="margin-top: 20px;">
            <template #header>
              <div class="card-header">
                <h3>修改密码</h3>
                <p>修改当前管理员账号密码</p>
              </div>
            </template>

            <el-form
              ref="passwordFormRef"
              :model="passwordForm"
              :rules="passwordRules"
              label-width="120px"
              :disabled="passwordLoading"
              class="password-form"
            >
              <el-form-item label="当前密码" prop="oldPassword" class="form-item-compact">
                <el-input
                  v-model="passwordForm.oldPassword"
                  type="password"
                  placeholder="请输入当前密码"
                  show-password
                  style="width: 300px"
                  clearable
                />
              </el-form-item>

              <el-form-item label="新密码" prop="newPassword" class="form-item-compact">
                <div class="password-input-wrapper">
                  <el-input
                    v-model="passwordForm.newPassword"
                    type="password"
                    placeholder="请输入新密码"
                    show-password
                    style="width: 300px"
                    clearable
                  />
                  <div class="password-strength" v-if="passwordForm.newPassword">
                    <el-progress
                      :percentage="passwordStrength"
                      :color="passwordStrengthColor"
                      :status="passwordStrengthStatus"
                      style="width: 300px"
                    />
                    <span class="strength-text">{{ passwordStrengthText }}</span>
                  </div>
                  <div class="password-tips">
                    <el-text type="info" size="small">
                      密码要求：<br/>
                      1. 长度为 8-20 个字符<br/>
                      2. 必须包含大写字母、小写字母和数字<br/>
                      3. 建议包含特殊字符 (!@#$%^&*)
                    </el-text>
                  </div>
                </div>
              </el-form-item>

              <el-form-item label="确认密码" prop="confirmPassword" class="form-item-compact">
                <el-input
                  v-model="passwordForm.confirmPassword"
                  type="password"
                  placeholder="请再次输入新密码"
                  show-password
                  style="width: 300px"
                  clearable
                />
              </el-form-item>

              <el-form-item class="form-item-submit">
                <el-button
                  type="primary"
                  @click="handleChangePassword"
                  :loading="passwordLoading"
                >
                  修改密码
                </el-button>
                <el-button @click="resetPasswordForm">
                  重置
                </el-button>
              </el-form-item>
            </el-form>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { User, Message, Phone, Upload, Check, RefreshLeft, Close } from '@element-plus/icons-vue'
import api from '@/utils/api'

// 表单引用
const profileFormRef = ref(null)

// 表单加载状态
const formLoading = ref(false)

// 用户信息（从localStorage或API获取）
const userInfo = ref({
  createdAt: '2024-01-01',
  lastLoginAt: '刚刚'
})

// 个人资料表单
const profileForm = reactive({
  username: 'admin',
  realName: '',
  email: '',
  phone: '',
  gender: 'male',
  role: 'admin',
  bio: '',
  avatar: ''
})

// 角色标签
const roleLabel = computed(() => {
  const roleMap = {
    'admin': '超级管理员',
    'manager': '管理员',
    'editor': '编辑员',
    'viewer': '查看员'
  }
  return roleMap[profileForm.role] || '未知角色'
})

// 表单验证规则
const profileRules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 3, max: 20, message: '长度在 3 到 20 个字符', trigger: 'blur' },
    { pattern: /^[a-zA-Z0-9_]+$/, message: '只能包含字母、数字和下划线', trigger: 'blur' }
  ],
  realName: [
    { required: true, message: '请输入真实姓名', trigger: 'blur' },
    { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
  ],
  email: [
    { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
  ],
  phone: [
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
  ]
}

// 密码表单相关
const passwordFormRef = ref(null)
const passwordLoading = ref(false)
const passwordForm = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
})

// 密码验证规则
const validatePassword = (rule, value, callback) => {
  if (value.length < 8 || value.length > 20) {
    callback(new Error('密码长度必须为 8-20 个字符'))
  } else if (!/[A-Z]/.test(value)) {
    callback(new Error('密码必须包含至少一个大写字母'))
  } else if (!/[a-z]/.test(value)) {
    callback(new Error('密码必须包含至少一个小写字母'))
  } else if (!/\d/.test(value)) {
    callback(new Error('密码必须包含至少一个数字'))
  } else {
    callback()
  }
}

const validateConfirmPassword = (rule, value, callback) => {
  if (value !== passwordForm.newPassword) {
    callback(new Error('两次输入的密码不一致'))
  } else {
    callback()
  }
}

const passwordRules = {
  oldPassword: [
    { required: true, message: '请输入当前密码', trigger: 'blur' }
  ],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { validator: validatePassword, trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: '请确认新密码', trigger: 'blur' },
    { validator: validateConfirmPassword, trigger: 'blur' }
  ]
}

// 密码强度计算
const passwordStrength = computed(() => {
  const password = passwordForm.newPassword
  if (!password) return 0
  
  let strength = 0
  
  // 长度
  if (password.length >= 8) strength += 20
  if (password.length >= 12) strength += 10
  if (password.length >= 16) strength += 10
  
  // 包含大写
  if (/[A-Z]/.test(password)) strength += 15
  
  // 包含小写
  if (/[a-z]/.test(password)) strength += 15
  
  // 包含数字
  if (/\d/.test(password)) strength += 15
  
  // 包含特殊字符
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 15
  
  return strength
})

const passwordStrengthColor = computed(() => {
  const strength = passwordStrength.value
  if (strength < 40) return '#F56C6C'
  if (strength < 70) return '#E6A23C'
  return '#67C23A'
})

const passwordStrengthStatus = computed(() => {
  const strength = passwordStrength.value
  if (strength < 40) return 'exception'
  if (strength < 70) return 'warning'
  return 'success'
})

const passwordStrengthText = computed(() => {
  const strength = passwordStrength.value
  if (strength < 40) return '弱'
  if (strength < 70) return '中'
  return '强'
})

// 修改密码
const handleChangePassword = async () => {
  if (!passwordFormRef.value) return
  
  // 防重复提交
  if (passwordLoading.value) {
    ElMessage.warning('正在处理中，请勿重复提交')
    return
  }
  
  await passwordFormRef.value.validate(async (valid) => {
    if (!valid) return
    
    passwordLoading.value = true
    
    try {
      // 调用API修改密码
      await api.changePassword({
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword
      })
      
      ElMessage.success('密码修改成功，请重新登录')
      
      // 重置表单
      resetPasswordForm()
      
      // 延迟2秒后退出登录
      setTimeout(() => {
        localStorage.removeItem('admin_token')
        localStorage.removeItem('admin_user')
        window.location.href = '/login'
      }, 2000)
    } catch (error) {
      console.error('修改密码失败:', error)
      const msg = error?.response?.data?.message || error?.message || '修改密码失败，请重试'
      ElMessage.error(msg)
    } finally {
      passwordLoading.value = false
    }
  })
}

// 重置密码表单
const resetPasswordForm = () => {
  passwordForm.oldPassword = ''
  passwordForm.newPassword = ''
  passwordForm.confirmPassword = ''
  if (passwordFormRef.value) {
    passwordFormRef.value.clearValidate()
  }
}

// 加载用户信息
const loadUserProfile = async () => {
  try {
    // 从API获取管理员个人信息
    const response = await api.getProfile()
    const userData = response.data
    
    // 更新个人资料表单
    Object.assign(profileForm, {
      username: userData.username || 'admin',
      realName: userData.realName || '',
      email: userData.email || '',
      phone: userData.phone || '',
      gender: userData.gender || 'male',
      role: userData.role || 'admin',
      bio: userData.bio || '',
      avatar: userData.avatar || ''
    })
    
    // 更新用户信息
    Object.assign(userInfo.value, {
      createdAt: userData.createdAt || '2024-01-01',
      lastLoginAt: userData.lastLoginAt || '刚刚'
    })
  } catch (error) {
    console.error('加载用户信息失败:', error)
    ElMessage.error('加载用户信息失败')
  }
}

// 处理头像更改（读取后压缩为 200x200，减小 base64 体积）
const handleAvatarChange = (file) => {
  const isImage = file.raw.type.startsWith('image/')
  const isLt2M = file.raw.size / 1024 / 1024 < 2

  if (!isImage) {
    ElMessage.error('只能上传图片文件！')
    return
  }
  if (!isLt2M) {
    ElMessage.error('图片大小不能超过 2MB！')
    return
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const MAX_SIZE = 200
      canvas.width = MAX_SIZE
      canvas.height = MAX_SIZE
      const ctx = canvas.getContext('2d')
      // 居中裁剪为正方形
      const side = Math.min(img.width, img.height)
      const sx = (img.width - side) / 2
      const sy = (img.height - side) / 2
      ctx.drawImage(img, sx, sy, side, side, 0, 0, MAX_SIZE, MAX_SIZE)
      profileForm.avatar = canvas.toDataURL('image/jpeg', 0.8)
      ElMessage.success('头像预览已更新，请保存修改')
    }
    img.src = e.target.result
  }
  reader.readAsDataURL(file.raw)
}

// 提交表单
const handleSubmit = async () => {
  if (!profileFormRef.value) return
  
  // 防重复提交
  if (formLoading.value) {
    ElMessage.warning('正在保存中，请勿重复提交')
    return
  }
  
  await profileFormRef.value.validate(async (valid) => {
    if (!valid) return
    
    formLoading.value = true
    
    try {
      // 调用API保存用户信息
      await api.updateProfile(profileForm)
      
      // 更新本地用户信息（含用户名）
      const stored = JSON.parse(localStorage.getItem('admin_user') || '{}')
      localStorage.setItem('admin_user', JSON.stringify({ ...stored, ...profileForm }))
      
      ElMessage.success('个人信息保存成功')
    } catch (error) {
      console.error('保存失败:', error)
      const msg = error?.response?.data?.message || error?.message || '保存失败，请重试'
      ElMessage.error(msg)
    } finally {
      formLoading.value = false
    }
  })
}

// 重置表单
const handleReset = () => {
  ElMessageBox.confirm(
    '确定要重置表单吗？所有未保存的修改将丢失',
    '提示',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).then(() => {
    loadUserProfile()
    ElMessage.info('表单已重置')
  }).catch(() => {
    // 取消操作
  })
}

// 取消编辑
const handleCancel = () => {
  window.history.back()
}

// 组件挂载时加载数据
onMounted(() => {
  loadUserProfile()
})
</script>

<style scoped>
.user-profile {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.header-left h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.page-subtitle {
  margin: 0;
  font-size: 14px;
  color: #909399;
}

.profile-content {
  margin-top: 20px;
}

/* 头像卡片样式 */
.avatar-card {
  margin-bottom: 20px;
}

.avatar-section {
  text-align: center;
  padding: 20px 0;
}

.user-avatar {
  margin-bottom: 16px;
  border: 3px solid #f5f7fa;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.avatar-uploader {
  margin: 16px 0;
}

.upload-btn {
  width: 120px;
}

.avatar-tips {
  margin-top: 16px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
}

.user-stats {
  margin-top: 20px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-label {
  font-size: 14px;
  color: #909399;
}

.stat-value {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

/* 信息卡片样式 */
.info-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .user-profile {
    padding: 12px;
  }
  
  .profile-content :deep(.el-col) {
    margin-bottom: 16px;
  }
}
</style>
